import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, Save, ArrowLeft, Send, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Toaster, toast } from "@/components/ui/sonner";
import PageHeader from "@/components/PageHeader";
import { getStoredToken } from "@/lib/axios-setup";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Pagina per la generazione del preventivo con Chat GPT.
 *
 * Accesso: solo utenti con flag `ai_assistant` (guardato lato backend).
 * Flusso:
 * 1. All'arrivo, legge il draft del piano di cura da `sessionStorage`
 *    (impostato dal bottone "Crea Preventivo e invia Piano di Cura").
 * 2. Chiama `POST /api/preventivi/generate` in modalità stream SSE — la
 *    prima chiamata (senza `user_message`) genera la bozza iniziale.
 * 3. L'utente può iterare via chat: ogni messaggio successivo raffina
 *    la bozza. La textarea grande a destra mostra il testo finale
 *    editabile a mano.
 * 4. Al click "Salva preventivo", `POST /api/preventivi/save` persiste
 *    in `db.preventivi` — poi redirect a `/preventivi` (archivio).
 */
export default function PreventivoChatPage() {
  const { opportunityId } = useParams();
  const navigate = useNavigate();

  const [draft, setDraft] = useState(null);          // payload del piano di cura
  const [bodyText, setBodyText] = useState("");       // testo del preventivo (editabile)
  const [messages, setMessages] = useState([]);        // storico chat: {role, content}
  const [inputMsg, setInputMsg] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [sessionId] = useState(() => `preventivo_${opportunityId}_${Date.now()}`);
  const abortRef = useRef(null);
  const bodyRef = useRef(null);

  // Load draft from sessionStorage on mount
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("preventivo_draft");
      if (!raw) {
        toast.error("Nessun piano di cura trovato — torna alla pagina Piano di Cura e riprova");
        setTimeout(() => navigate(-1), 2500);
        return;
      }
      const parsed = JSON.parse(raw);
      if (parsed.opportunity_id !== opportunityId) {
        toast.error("Il piano di cura non corrisponde a questa opportunità");
        return;
      }
      setDraft(parsed);
      // Trigger initial draft generation
      generateInitialDraft(parsed);
    } catch (e) {
      console.error(e);
      toast.error("Errore nel caricamento del piano di cura");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opportunityId]);

  const streamRequest = async ({ userMessage, piano }) => {
    setIsStreaming(true);
    setBodyText("");
    let accumulated = "";
    try {
      const controller = new AbortController();
      abortRef.current = controller;
      // Attacca manualmente il Bearer token (fetch non passa dagli
      // interceptor axios, quindi Authorization header serve esplicito).
      // I cookies HttpOnly funzionano come fallback via credentials="include".
      const headers = { "Content-Type": "application/json" };
      const token = getStoredToken();
      if (token) headers.Authorization = `Bearer ${token}`;
      const res = await fetch(`${API}/api/preventivi/generate`, {
        method: "POST",
        credentials: "include",
        headers,
        body: JSON.stringify({
          opportunity_id: piano.opportunity_id,
          session_id: sessionId,
          patient_name: piano.patient_name,
          insurance: piano.insurance,
          reparto: piano.reparto,
          piano_cura: piano.piano_cura,
          user_message: userMessage || null,
        }),
        signal: controller.signal,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        // Parse SSE lines
        const lines = buffer.split("\n\n");
        buffer = lines.pop() || "";
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const content = line.slice(6);
          if (content === "[DONE]") continue;
          if (content.startsWith("[ERROR]")) {
            toast.error(content);
            continue;
          }
          accumulated += content.replace(/\\n/g, "\n");
          setBodyText(accumulated);
        }
      }
    } catch (e) {
      if (e.name !== "AbortError") {
        toast.error(e.message || "Errore nella generazione del preventivo");
      }
    } finally {
      setIsStreaming(false);
      abortRef.current = null;
    }
    return accumulated;
  };

  const generateInitialDraft = async (piano) => {
    setMessages([{ role: "system", content: "Bozza iniziale generata dal piano di cura" }]);
    const result = await streamRequest({ userMessage: null, piano });
    if (result) {
      setMessages((m) => [...m, { role: "assistant", content: result }]);
    }
  };

  const sendMessage = async () => {
    const msg = inputMsg.trim();
    if (!msg || isStreaming || !draft) return;
    setInputMsg("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    const result = await streamRequest({ userMessage: msg, piano: draft });
    if (result) {
      setMessages((m) => [...m, { role: "assistant", content: result }]);
    }
  };

  const regenerate = async () => {
    if (!draft || isStreaming) return;
    setMessages([{ role: "system", content: "Bozza rigenerata dal piano di cura" }]);
    const result = await streamRequest({ userMessage: null, piano: draft });
    if (result) {
      setMessages((m) => [...m, { role: "assistant", content: result }]);
    }
  };

  const savePreventivo = async () => {
    if (!bodyText.trim() || !draft) {
      toast.error("Il preventivo è vuoto");
      return;
    }
    setIsSaving(true);
    try {
      const payload = {
        opportunity_id: draft.opportunity_id,
        session_id: sessionId,
        patient_name: draft.patient_name,
        patient_id: draft.patient_id,
        insurance: draft.insurance,
        reparto: draft.reparto,
        total_euro: draft.total_euro,
        body_text: bodyText,
        piano_cura_snapshot: draft.piano_cura,
      };
      await axios.post(`${API}/api/preventivi/save`, payload, { withCredentials: true });
      toast.success("Preventivo salvato nell'archivio");
      try { sessionStorage.removeItem("preventivo_draft"); } catch { /* ignore */ }
      setTimeout(() => navigate("/preventivi"), 800);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f5f7fa] bg-grid-light pt-[calc(7px+3.5rem+13px)]" data-testid="preventivo-chat-page">
      <Toaster position="top-right" theme="light" />
      <div className="max-w-7xl mx-auto px-4 lg:px-6 pb-8">
        <div className="flex items-center gap-3 mb-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Torna al Piano
          </Button>
          <div className="flex-1" />
          <Button variant="outline" size="sm" onClick={regenerate} disabled={isStreaming} data-testid="regenerate-btn">
            <RefreshCw className={`w-4 h-4 mr-1 ${isStreaming ? "animate-spin" : ""}`} /> Rigenera bozza
          </Button>
          <Button onClick={savePreventivo} disabled={isSaving || isStreaming || !bodyText.trim()} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="save-preventivo-btn">
            {isSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Salva preventivo
          </Button>
        </div>

        <PageHeader
          title="Preventivo AI"
          subtitle={draft ? `${draft.patient_name} · ${draft.insurance} · ${draft.reparto} · Totale €${draft.total_euro?.toFixed(2)}` : "Caricamento…"}
        />

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 mt-6">
          {/* Chat (sinistra 2/5) */}
          <Card className="lg:col-span-2 border-slate-200 shadow-sm">
            <CardContent className="p-4 flex flex-col h-[70vh]">
              <div className="flex-1 overflow-y-auto space-y-3 pr-1" data-testid="chat-messages">
                {messages.map((m, i) => (
                  <div key={i} className={`text-sm rounded-lg px-3 py-2 ${
                    m.role === "user"
                      ? "bg-violet-50 border border-violet-100 text-violet-900 ml-6"
                      : m.role === "assistant"
                        ? "bg-slate-50 border border-slate-200 text-slate-700 mr-6 whitespace-pre-wrap"
                        : "text-xs text-slate-400 italic"
                  }`}>
                    {m.content.length > 400 && m.role === "assistant"
                      ? m.content.slice(0, 400) + "…"
                      : m.content}
                  </div>
                ))}
                {isStreaming && (
                  <div className="text-xs text-slate-400 italic flex items-center gap-2">
                    <Loader2 className="w-3 h-3 animate-spin" /> Sto scrivendo…
                  </div>
                )}
              </div>
              <div className="pt-3 border-t border-slate-200 mt-3 space-y-2">
                <Textarea
                  value={inputMsg}
                  onChange={(e) => setInputMsg(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                      e.preventDefault();
                      sendMessage();
                    }
                  }}
                  placeholder="Chiedi modifiche… (es. 'rendi più formale', 'aggiungi una nota sulle rate') · Ctrl+Enter per inviare"
                  className="min-h-[70px] text-sm resize-none"
                  disabled={isStreaming}
                  data-testid="chat-input"
                />
                <div className="flex justify-end">
                  <Button
                    size="sm"
                    onClick={sendMessage}
                    disabled={isStreaming || !inputMsg.trim()}
                    data-testid="send-chat-btn"
                  >
                    <Send className="w-4 h-4 mr-1" /> Invia
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Editor testo preventivo (destra 3/5) */}
          <Card className="lg:col-span-3 border-slate-200 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-slate-700">Testo preventivo</h3>
                <span className="text-xs text-slate-400">
                  {bodyText.length} caratteri · editabile
                </span>
              </div>
              <Textarea
                ref={bodyRef}
                value={bodyText}
                onChange={(e) => setBodyText(e.target.value)}
                placeholder={isStreaming ? "Generazione in corso…" : "Il testo del preventivo apparirà qui."}
                className="min-h-[60vh] text-sm font-mono resize-none whitespace-pre-wrap"
                data-testid="preventivo-body"
              />
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
